import { useBaseStore } from '@common/stores/storage';
import { isRealOnline, isStageFsim } from '@common/utils/env';

const getDefaultReportProperties = () => {
  const { appKey, cooperatorId, productCode } = useBaseStore();
  return {
    app_key: appKey,
    channel_code: cooperatorId,
    product_code: productCode,
    pathname: location.pathname,
  };
};

const projectCodes = {
  credit: 'finance_process',
  withdraw: 'finance_process',
  repay: 'finance_process',
  public: 'finance_process',
  pollen: 'terminal_applications',
};

function getControlledPromise(fn) {
  let _resolve;
  let _reject;

  const _p = new Promise((resolve, reject) => {
    _resolve = resolve;
    _reject = reject;
    fn?.(resolve, reject);
  });

  _p.resolve = _resolve;
  _p.reject = _reject;

  return _p;
}

const ABTestReady = getControlledPromise();
function removeUnlessProperty(params) {
  try {
    delete params.abkvs;
  } catch (e) {
    console.log(e);
  }
}

const formatABKV = (abItems = {}) => {
  let keys = Object.keys(abItems) || [];
  let default_values = Object.values(abItems) || [];
  if (keys?.length < 1) {
    throw new Error('多ab试验传参时，ab键值对规则不符合规范');
  }
  return { keys, default_values };
};
let formatABResult = function (result, params, isMutil) {
  let abResult = {};
  if (isMutil) {
    result.forEach(function (res, index) {
      abResult[params.key[index]] = res && res.val;
    });
  } else {
    abResult = result.val;
  }
  return abResult;
};
/**
 *
 * @author  lijun.wang01
 * @create 2023-12-20 2:57
 * @note 简单描述该文件作用
 **/
export const useNewAB = (initProps = {}) => {
  try {
    // 区分测试、生产、仿真
    const isFsim = isStageFsim();
    const isEnvProduction = isRealOnline();
    let BaseURL, report_url;
    if (isEnvProduction) {
      BaseURL = 'https://abtest.xiaolongcloud.com';
      report_url = 'https://ubs.xiaolongcloud.com/ubs?project=default';
    } else if (isFsim) {
      BaseURL = 'http://abtest-fsim.xiaolongcloud.local';
      report_url = 'https://ubs-fsim.xiaolongcloud.local/sa?project=default';
    } else {
      BaseURL = 'http://abtest-diversion-testsims-8080.msxf.msxfyun.test';
      report_url = 'http://ubstest.msxf.test:8106/sa?project=999';
    }
    window.ab = window.collectEvent?.('init', {
      BaseURL,
      reportOption: {
        // 此处等到ab的sdk修改为数组之后，还得改回去
        report_url,
        report_event_name: '$ABTestExposure',
      },
      // ms 默认时间，网关超时时间也是30000
      pullAbTestConfigsTimeout: 30000,
      enable_ab_test: true,
      projectCodes: [
        initProps?.['projectCodes'] || projectCodes[process.env.APP_NAME] || 'finance_process',
      ],
      user_unique_id: initProps?.['userId'] || useBaseStore().userId,
      // 初始化时是否自动获取一次实验分流
      init_pull_ab_config: true,
      specificKeys: ['vayhFrontendLogResponseMaxLength'].concat(initProps?.['specificKeys'] || []),
    });
    ABTestReady.resolve();
  } catch (e) {
    console.error(e``);
    ABTestReady.reject();
  }
};

export const getABBasic = (abType) => {
  /**
   * @params params 为string: 为试验的key，此时defaultValue就是该实验默认值;如果params 为object类型:那么就说明你这个有自定义倾向啊
   * @defaultValue   如果不存在，该试验的返回值为undefined;
   * @params timeout 超时时间，默认5000，若不需要，可设置为0
   * */
  return function (params, defaultValue, timeout = 5000) {
    let data = {};
    // 说明是获取单个的
    if (typeof params === 'string') {
      data.key = params;
      // 咱就说你不传，那就是undefined
      data.default_value = defaultValue;
    } else if (typeof params === 'object') {
      data = params;
      let abKeyValues = params?.abkvs;
      // 获取多个试验
      if (abKeyValues) {
        let { keys, default_values } = formatABKV(abKeyValues);
        params.key = keys;
        params.default_value = default_values.map((v) => ({ val: v }));
      }
    }
    // 是同时获取多个试验或者feature；
    let isMutil = Array.isArray(data.key);
    let {
      successCB = () => {},
      type,
      default_value,
      customProperties = {},
      reportProperties = {},
    } = data;

    // 在生成默认值的时候，已经添加过val结构；
    if (!isMutil) {
      default_value = { val: default_value };
    }

    // 超时定时器
    let timer = null;

    // 记录执行完成resolve
    let doneResolve = null;
    // 执行完成逻辑
    function done(result = default_value, paramsObj = params) {
      if (timer) {
        clearTimeout(timer);
        timer = null;
      }
      let abResult = formatABResult(result, paramsObj, isMutil);
      doneResolve?.(abResult);
      try {
        successCB(abResult);
      } catch (e) {
        // ignore
      }
    }

    // 是否定义了超时时间
    if (!isNaN(timeout) && timeout > 0) {
      timer = setTimeout(() => {
        // 增加abtest请求超时日志
        window.AdReach?.logger?.warn({
          time: Date.now(),
          message: `ABTest请求超时[timeout=${timeout}]`,
          data,
        });
        done();
      }, timeout);
    }

    const defaultProps = getDefaultReportProperties();
    // 判断公共属性是否存在为空的数据
    if (Object.values(defaultProps).some((value) => (value ?? '') === '')) {
      window.AdReach?.logger?.warn({
        time: Date.now(),
        message: `ABTest公共属性部分或全部不存在`,
        props: defaultProps,
        url: `${location.protocol}//${location.host}${location.pathname}`,
        data,
      });
    }

    return new Promise((resolve) => {
      doneResolve = resolve;
      let p = Object.assign(
        data,
        {
          default_value,
          customProperties: {
            ...defaultProps,
            ...customProperties,
          },
          reportProperties: {
            ...defaultProps,
            ...reportProperties,
          },
          successCallback(result) {
            done(result);
          },
          //网络错误、请求URL不对，就会走此回调
          errorCallback(defaultValue, error) {
            // 增加abtest请求失败日志
            window.AdReach?.logger?.warn({
              time: Date.now(),
              message: `ABTest请求失败: ${error?.errorMessage}`,
              data,
            });
            done(defaultValue);
          },
        },
        type ? { type } : {},
      );
      removeUnlessProperty(p);
      // sdk是否初始化成功
      if (typeof window.ab?.[abType] === 'function') {
        window.ab[abType](p);
      } else {
        // 如果获取AB链接写错，这个地方的执行会导致执行多次执行done，所以初始化失败，是不是可以把这个去掉，留下一个未解决的promise；
        done();
      }
    });
  };
};
/**  优先读取本地缓存，缓存不存在时从服务端获取数据 */
export const getABCacheOrOnline = getABBasic('fastPullABTest');
/**  读取本地缓存，缓存不存在时使用默认值 */
export const getABCacheOrDefault = getABBasic('pullCacheABTest');
/**  忽略本地缓存，从服务端获取数据 */
export const getABOnline = getABBasic('pullABTest');

/**
 *
 * @returns {Promise<boolean>}
 */
export const waitABTestReady = async () => {
  return new Promise((_resolve) => {
    ABTestReady.then(() => _resolve(true)).catch(() => _resolve(false));
  });
};
